// src/publish/pieces/linkedinCarousel.js — pieces of the LinkedIn carousel (format key 'linkedin-carousel').
//
// A carousel is a PDF document posted with a companion text. The backend
// sends the slides twice: `slides[]` (number, type cover/content/cta,
// headline, body, visual_cue — already structured) and `body` (the Writer's
// raw "## Slide N / **Headline:** …" markup the backend derived slides[] from).
// ⛔ Only slides[] is read here; `body` is never parsed. Pieces, in order:
//   companion   primary, 'plain' — hook (the post text) + cta + the hashtags
//               as one last "#a #b" paragraph; the text that publishes the document
//   slide_N     one per slides[] entry, 'plain', label "Slide N" (labelId 'slide',
//               destinationId 'slide'), ONE slide block: number (the backend's,
//               else the position), slideType when it is cover/content/cta,
//               headline runs, body blocks, visual cue. Its plain text reads
//               "Slide N · Tipo / headline / body / Indicazione visiva: …".
//               Ids are unique: a number the backend repeats falls back to the
//               slide's position (and on from there) for the id AND the label,
//               so two copy buttons never share a key.
//   hashtags    'tags' — the same hashtags on their own, to paste separately
// A slide with neither headline, body nor cue gives no piece; a missing or
// empty slides[] gives no slide pieces. The primary is companion, else the
// first piece — exactly one. serialize/csv.js reads the slide blocks of these
// pieces to write one CSV row per slide.

import { slide, SLIDE_TYPES } from '../blocks.js';
import { normalizeText, inlineRuns } from '../normalize.js';
import { makePiece, tagsBlocks, ensureOnePrimary, trimmed as str, arr } from '../piece.js';

const FORMAT = 'linkedin-carousel';

/** The slide's number: the backend's when usable and not yet taken, else the first free position. */
function slideNumber(raw, index, taken) {
  const n = Number(raw.number);
  let number = Number.isInteger(n) && n > 0 && !taken.has(n) ? n : index + 1;
  while (taken.has(number)) number += 1;
  taken.add(number);
  return number;
}

function slidePiece(raw, index, taken) {
  if (!raw || typeof raw !== 'object') return null;
  const headline = inlineRuns(str(raw.headline));
  const body = normalizeText(str(raw.body));
  const visualCue = str(raw.visual_cue) || null;
  if (!headline.length && !body.length && !visualCue) return null;
  const number = slideNumber(raw, index, taken);
  const slideType = SLIDE_TYPES.includes(raw.type) ? raw.type : null;
  return makePiece(FORMAT, {
    id: `slide_${number}`,
    labelId: 'slide',
    labelParams: { n: number },
    destinationId: 'slide',
    blocks: [slide({ number, slideType, headline, body, visualCue })],
  });
}

export function build(entry) {
  const content = entry?.results?.content;
  if (!content || typeof content !== 'object') return [];
  const pieces = [];

  const tags = tagsBlocks(content.hashtags);
  const companion = [...normalizeText(str(content.hook)), ...normalizeText(str(content.cta)), ...tags];
  if (companion.length) pieces.push(makePiece(FORMAT, { id: 'companion', blocks: companion }));

  const taken = new Set();
  arr(content.slides).forEach((raw, i) => {
    const piece = slidePiece(raw, i, taken);
    if (piece) pieces.push(piece);
  });

  if (tags.length) pieces.push(makePiece(FORMAT, { id: 'hashtags', kind: 'tags', blocks: tags }));

  return ensureOnePrimary(pieces, ['companion']);
}
